import { assoc, compose, lensProp, lens, view, lensPath } from 'ramda';
import { differenceInDays, addDays } from 'date-fns/esm';
import { isClaimedPromotion, linkify } from './utils';

export const typenameLens = lensProp('__typename');
const claimedAtLens = lensProp('claimedAt');
export const idLens = lensProp('id');
export const nameLens = lensProp('name');
export const codeLens = lensProp('code');

export const promotionIdLens = lensPath(['promotion', 'id']);
export const promotionCodeLens = lensPath(['promotion', 'code']);
export const promotionPlacementsLens = lensPath(['promotion', 'placements']);
export const promotionCategoriesLens = lensPath(['promotion', 'categories']);
export const promotionMetadataLens = lensPath(['promotion', 'metadata']);

export const createCloudinaryImageLens = (height, width) =>
  lens(
    ({ baseUrl, id, url } = {}) =>
      baseUrl && id
        ? `${baseUrl}/w_${width * 1.5},h_${height * 1.5},c_fit,q_auto/${id}.jpg`
        : url,
    image => image
  );

export const addressLens = lens(
  a =>
    a
      ? `<span class="address-line-one">${a.street}</span>
    <span class="address-line-two">${a.city}, ${a.state} ${a.postalCode}</span>`
      : '',
  assoc('address')
);

export const barcodeLens = compose(
  promotionMetadataLens,
  lensPath(['redemption', 'barcodeImage', 'url'])
);

export const entityNameLens = lens(
  entity => (entity.metadata && entity.metadata.displayName) || entity.name,
  // no setting with this lens
  entity => entity
);

export const exclusiveLens = compose(
  promotionMetadataLens,
  lensProp('exclusive')
);

const legacyRedemptionDescriptionLens = compose(
  promotionMetadataLens,
  lensPath(['redemption', 'description'])
);
const redemptionDescriptionLens = compose(
  lensProp('redemption'),
  lens(
    redemption => redemption && redemption.methods && redemption.methods[0],
    () => redemption
  ),
  lensProp('description')
);

//this is deprecated. Do not use
export const descriptionLens = lens(
  claimedPromo => {
    const description =
      view(redemptionDescriptionLens, claimedPromo) ||
      view(legacyRedemptionDescriptionLens, claimedPromo) ||
      `To redeem, present this code at checkout`;

    return `<p class="body-text">${linkify(description, claimedPromo)}</p>`;
  },
  claimedPromo => claimedPromo
);

//use this one instead
export const newDescriptionLens = lens(
  promo => {
    const description =
      view(redemptionDescriptionLens, promo) ||
      view(redemptionDescriptionLens, promo.promotion);

    return `<p class="body-text">${linkify(description, promo)}</p>`;
  },
  promo => promo
);

const promotionMetadataCodeLens = compose(
  promotionMetadataLens,
  codeLens
);

const legacyCodeLens = lens(
  claimedPromo => {
    return (
      view(promotionMetadataCodeLens, claimedPromo) ||
      claimedPromo.confirmationNumber
    );
  },
  claimedPromo => claimedPromo
);

export const promotionHasNoneCodeType = promo =>
  view(lensPath(['redemption', 'code', 'type']), promo) === 'none';

export const promoCodeLens = lens(
  claimedPromo => {
    if (promotionHasNoneCodeType(claimedPromo)) {
      return `<span class="promotions-no-code></span>`;
    }
    const barcodeURL = view(barcodeLens, claimedPromo);
    return barcodeURL
      ? `<img src=${barcodeURL} />`
      : `<span>${view(redemptionCodeLens, claimedPromo) ||
          view(legacyCodeLens, claimedPromo)}</span>`;
  },
  // not for setting
  claimedPromo => claimedPromo
);

export const promotionDisplayNameLens = compose(
  promotionMetadataLens,
  lensProp('displayName')
);

export const promotionImageUrlLens = compose(
  lensPath(['promotion', 'image']),
  createCloudinaryImageLens(184, 368)
);

export const fullTermsLens = compose(
  promotionMetadataLens,
  lensProp('fullTerms')
);
export const urlLens = compose(
  promotionMetadataLens,
  lensProp('url')
);

export const pillTextLens = lens(
  promo => {
    if (isClaimedPromotion(promo)) {
      return 'Claimed';
    }

    return view(
      compose(
        promotionMetadataLens,
        lensProp('discount')
      ),
      promo
    );
  },
  promo => promo
);

export const redemptionUrlLens = compose(
  lensProp('redemption'),
  lensProp('methods'),
  lens(
    (redemptionMethods = []) =>
      view(
        lensProp('value'),
        redemptionMethods.filter(method => method.type === 'website')[0]
      ),
    methods => methods
  )
);

export const entityLogoLens = compose(
  lens(
    promo => (isClaimedPromotion(promo) ? promo.claimedFor : promo.validFor[0]),
    promo => promo
  ),
  lensProp('logo'),
  createCloudinaryImageLens(80, 350)
);

export const isBBBUrl = promo =>
  (view(redemptionUrlLens, promo) || view(urlLens, promo) || '').includes(
    'bedbathandbeyond'
  );
export const isBBBCode = promo =>
  (view(lensPath(['promotion', 'code']), promo) || '') === 'BBBY20OFF80';
export const isHACLCode = promo =>
  (view(lensPath(['promotion', 'code']), promo) || '') === 'HACL25';
export const isHATVCode = promo =>
  (view(lensPath(['promotion', 'code']), promo) || '') === 'HATV20';
export const isMarcus = promo =>
  (view(lensPath(['promotion', 'name']), promo) || '')
    .toLowerCase()
    .includes('marcus');
export const isAmazon = promo =>
  (view(lensPath(['promotion', 'code']), promo) || '') === 'AMAZON10OFF';
export const isWalmart = promo =>
  (view(lensPath(['promotion', 'code']), promo) || '').includes('WALMART');
export const isSamsClub = promo =>
  (view(lensPath(['promotion', 'code']), promo) || '').includes('SAMSCLUB');

export const getPillText = promo => {
  const claimedAt = new Date(view(claimedAtLens, promo));
  const expiry = addDays(claimedAt, 30);
  if (isBBBUrl(promo)) {
    const difference = differenceInDays(expiry, Date.now());
    return difference > 0
      ? `Expires in ${difference} Day${difference > 1 ? 's' : '!'}`
      : 'Expired';
  } else if (isMarcus(promo)) {
    return view(
      compose(
        promotionMetadataLens,
        lensProp('discount')
      ),
      promo
    );
  }
  return 'Claimed';
};

export const redemptionCodeLens = lensPath(['redemption', 'code', 'value']);

export const shortTermsLens = compose(
  promotionMetadataLens,
  lensProp('shortTerms')
);

export const hasFullTerms = promo => Boolean(view(fullTermsLens, promo));
